import { apiClient } from './apiClient';
import { DataCleaningConfig, ProcessingResult } from '../../types/dataProcessing';
import { PreprocessingConfig } from '../../types/preprocessing';

import { DatasetStatistics } from '../../types/statistics';
import type { ExportOptions } from '../../types/export';
import { QualityCheckResult } from '../../types/quality';
import { TransformationRule } from '../../types/transformation';
import { ValidationRule, ValidationResult } from '../../types/validation';

interface ProcessingOptions {
  protectedColumns?: string[];
  excludedColumns?: string[];
  delimiter?: string;
}

interface ResponseData {
    downloadUrl: string | undefined;
    previewData: any[]; 
}

const PYTHON_API_URL = process.env.REACT_APP_PYTHON_API_URL || 'http://localhost:5000';

export class DataProcessingService {
  public async uploadFile(file: File, options: DataCleaningConfig) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    formData.append('sample', "false");
    try {
        
      const response = await apiClient.post<ResponseData>('/api/data-processing/clean', formData);
      
      return response
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  }

  public async getProcessingStatus(jobId: string) {
    return apiClient.get<{ status: string; progress: number }>(`/api/data-processing/status/${jobId}`);
  }

  public async processSample(file: File, options: DataCleaningConfig) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    formData.append('sample', "true");
    // formData.append('options', JSON.stringify(options));

    try {
        const response = await apiClient.post<ResponseData>('/api/data-processing/clean', formData);
        
    //   await fetch(`${apiClient}/api/data-processing/sample`, {
    //     method: 'POST',
    //     body: formData,
    //   });

    //   if (!response.ok) {
    //     throw new Error('Sample processing failed');
    //   }

        return response;
    } catch (error) {
        console.error('Sample processing error:', error);
        throw error;
    }
  }

  public async preprocessSampleFile(file: File, options: PreprocessingConfig) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    formData.append('sample', "true");

    try {
      const response = await apiClient.post<ResponseData>('/api/data-processing/preprocess', formData);
      return response;
    } catch (error) {
        console.error('Sample Preprocessing error:', error);
      throw error;
    }
    
  }

  public async preprocessFullFile(file: File, options: PreprocessingConfig) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    formData.append('sample', "false");

    try {
      const response = await apiClient.post<ResponseData>('/api/data-processing/preprocess', formData);
      return response;
    } catch (error) {
        console.error('Preprocessing error:', error);
      throw error;
    }
    
  }

  public async getDatasetStatistics(file: File): Promise<DatasetStatistics> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${PYTHON_API_URL}/statistics`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to get dataset statistics');
      }

      return await response.json();
    } catch (error) {
      console.error('Statistics error:', error);
      throw error;
    }
  }

  public async exportData(data: any[], options: ExportOptions): Promise<Blob> {
    try {
      const response = await fetch(`${apiClient}/data-processing/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data, options }),
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      return await response.blob();
    } catch (error) {
      console.error('Export error:', error);
      throw error;
    }
  }

  public async checkDataQuality(file: File): Promise<QualityCheckResult> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${apiClient}/data-processing/quality-check`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Quality check failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Quality check error:', error);
      throw error;
    }
  }

  public async applyTransformations(file: File, rules: TransformationRule[]): Promise<any[]> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('rules', JSON.stringify(rules));

    try {
      const response = await fetch(`${apiClient}/data-processing/transform`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Transformation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Transformation error:', error);
      throw error;
    }
  }

  public async previewTransformation(data: any[], rule: TransformationRule): Promise<any[]> {
    try {
      const response = await fetch(`${apiClient}/data-processing/transform-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data, rule }),
      });

      if (!response.ok) {
        throw new Error('Transform preview failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Transform preview error:', error);
      throw error;
    }
  }

  public async validateData(file: File, rules: ValidationRule[]): Promise<ValidationResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('rules', JSON.stringify(rules));

    try {
      const response = await fetch(`${apiClient}/data-processing/validate`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Validation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Validation error:', error);
      throw error;
    }
  }

  public async previewValidation(data: any[], rule: ValidationRule): Promise<ValidationResult> {
    try {
      const response = await fetch(`${apiClient}/data-processing/validate-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data, rule }),
      });

      if (!response.ok) {
        throw new Error('Validation preview failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Validation preview error:', error);
      throw error;
    }
  }

  public async cleanData(file: File, options: any): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));

    try {
      const response = await fetch(`${PYTHON_API_URL}/clean`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Data cleaning failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Cleaning error:', error);
      throw error;
    }
  }

  public async augmentData(data: any[], type: string, params: any): Promise<any[]> {
    try {
      const response = await fetch(`${PYTHON_API_URL}/augment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data, type, params }),
      });

      if (!response.ok) {
        throw new Error('Data augmentation failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Augmentation error:', error);
      throw error;
    }
  }
}

export const dataProcessingService = new DataProcessingService(); 